import React from 'react';
import { RocketIcon, CalculatorIcon, MonitorIcon, GraduationCapIcon, UsersIcon, ClipboardCheckIcon, SparklesIcon, ArrowRightIcon } from 'lucide-react';
const benefits = [{
  icon: RocketIcon,
  tag: 'Pronto in 5 min',
  title: "Lezioni Pronte all'Uso",
  desc: "Dall'aritmetica agli integrali, tutto strutturato per ragazzi dagli 11 ai 19 anni. Apri il file e sei pronto.",
  accent: 'cta-green'
}, {
  icon: CalculatorIcon,
  tag: '8 volumi risolti',
  title: 'Soluzionari Completi',
  desc: 'Ogni singolo esercizio è già risolto e commentato passo passo. Risparmia ore di calcoli manuali.',
  accent: 'primary'
}, {
  icon: MonitorIcon,
  tag: 'Slide LIM incluse',
  title: 'Supporto Visivo per la LIM',
  desc: 'Slide grafiche ad alto impatto pronte da proiettare: apri, mostra, insegna in modo magico.',
  accent: 'secondary'
}, {
  icon: GraduationCapIcon,
  tag: 'Conforme MIUR',
  title: "Metodologia d'Eccellenza",
  desc: 'Un sistema che garantisce la copertura del curricolo MIUR con la metodologia EDULABS™.',
  accent: 'cta-green'
}, {
  icon: UsersIcon,
  tag: 'DSA & BES',
  title: 'Didattica Inclusiva',
  desc: 'Materiali differenziati per livello (Base, Intermedio, Avanzato) e schede dedicate a DSA e BES.',
  accent: 'primary'
}, {
  icon: ClipboardCheckIcon,
  tag: 'Pronte da stampare',
  title: 'Verifiche & Griglie',
  desc: 'Verifiche modulari con rubriche di valutazione già conformi alle linee guida ministeriali.',
  accent: 'secondary'
}];
const accentMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  glow: string;
  ring: string;
}> = {
  'cta-green': {
    bg: 'bg-cta-green/10',
    text: 'text-cta-green',
    border: 'border-cta-green/30',
    glow: 'shadow-[0_0_30px_rgba(16,185,129,0.25)]',
    ring: 'group-hover:border-cta-green/60'
  },
  primary: {
    bg: 'bg-primary/10',
    text: 'text-primary',
    border: 'border-primary/30',
    glow: 'shadow-[0_0_30px_rgba(234,88,12,0.20)]',
    ring: 'group-hover:border-primary/60'
  },
  secondary: {
    bg: 'bg-secondary/10',
    text: 'text-secondary',
    border: 'border-secondary/30',
    glow: 'shadow-[0_0_30px_rgba(245,158,11,0.20)]',
    ring: 'group-hover:border-secondary/60'
  }
};
export function BenefitsSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-1">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} data-id="element-2"></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.05) 0%, transparent 70%)'
    }} data-id="element-3"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4">
        <div className="text-center mb-10 sm:mb-16" data-id="element-5">
          <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-cta-green uppercase tracking-[0.2em] mb-3 sm:mb-4 bg-cta-green/10 border border-cta-green/20 px-4 py-1.5 rounded-full" data-id="element-6">
            <SparklesIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-7" />
            Tutto incluso nel Kit
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-8">
            Cosa otterrai con
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cta-green to-emerald-300" data-id="element-9">
              {' '}
              Matematica Pronta
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed" data-id="element-10">
            6 vantaggi concreti che trasformano il modo in cui prepari, insegni
            e valuti la matematica.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-id="element-11">
          {benefits.map((benefit, i) => {
          const a = accentMap[benefit.accent];
          const Icon = benefit.icon;
          return <div key={i} className={`bg-bg-alt p-6 sm:p-7 rounded-2xl border border-border-light flex flex-col gap-4 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group ${a.ring} hover:${a.glow}`} data-id="element-12">
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-current to-transparent ${a.text} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} data-id="element-13"></div>

                <div className="flex items-start justify-between gap-3" data-id="element-14">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${a.bg} border ${a.border} flex items-center justify-center flex-shrink-0`} data-id="element-15">
                    <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${a.text}`} strokeWidth={2.2} data-id="element-16" />
                  </div>
                  <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${a.bg} ${a.text} border ${a.border} whitespace-nowrap`} data-id="element-17">
                    {benefit.tag}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-black text-text-main leading-tight" data-id="element-18">
                  {benefit.title}
                </h3>
                <p className="text-text-sec leading-relaxed text-sm sm:text-base flex-1" data-id="element-19">
                  {benefit.desc}
                </p>
              </div>;
        })}
        </div>

        <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 bg-bg-alt border border-border-light rounded-2xl px-6 py-5 sm:px-10 sm:py-6 max-w-3xl mx-auto" data-id="element-20">
          <div className="flex items-center gap-3 text-text-main" data-id="element-21">
            <div className="w-10 h-10 rounded-full bg-cta-green/15 border border-cta-green/30 flex items-center justify-center flex-shrink-0" data-id="element-22">
              <SparklesIcon className="w-5 h-5 text-cta-green" strokeWidth={2.5} data-id="element-23" />
            </div>
            <p className="font-bold text-sm sm:text-base" data-id="element-24">
              + 6 Bonus esclusivi del valore di{' '}
              <span className="text-cta-green" data-id="element-25">€ 87</span> inclusi
            </p>
          </div>
          <a href="#pricing" className="text-cta-green font-bold text-sm sm:text-base flex items-center gap-1.5 hover:gap-3 transition-all whitespace-nowrap" data-id="element-26">
            Vedi l'offerta
            <ArrowRightIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-27" />
          </a>
        </div>
      </div>
    </section>;
}